import AsyncStorage from '@react-native-async-storage/async-storage';
import * as ImagePicker from 'expo-image-picker';
import { Platform } from 'react-native';
import {
  addSentryBreadcrumb,
  captureSentryExceptionWithScope,
} from '@/services/monitoring/sentry';
import { PendingImagePickerRecoveryLogger } from '@/utils/logger';

export type PendingCameraContext = {
  flowId: string;
  source: string | null;
  screen: string | null;
  action: string | null;
  entityType: string | null;
  entityId: string | null;
  checklistId: string | null;
  empresaId: string | null;
  routePath: string | null;
  createdAt: number;
};

export type RecoveredCameraAssetRecord = {
  context: PendingCameraContext;
  asset: ImagePicker.ImagePickerAsset;
  recoveredAt: number;
};

type PendingRecoveryOutcome =
  | { status: 'unsupported' }
  | { status: 'no_context' }
  | { status: 'empty'; context: PendingCameraContext }
  | { status: 'stored'; context: PendingCameraContext; routePath: string }
  | { status: 'ignored'; context: PendingCameraContext | null }
  | { status: 'failed'; error: Error };

const PENDING_CAMERA_CONTEXT_KEY = '@app_guincho/pending_camera_context';
const RECOVERED_CAMERA_ASSET_KEY_PREFIX = '@app_guincho/recovered_camera_asset:';
const PENDING_CONTEXT_TTL_MS = 15 * 60 * 1000;
const RECOVERED_ASSET_TTL_MS = 30 * 60 * 1000;

const asString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value : null;

const buildRecoveredCameraAssetKey = (checklistId: string, fieldId: string): string =>
  `${RECOVERED_CAMERA_ASSET_KEY_PREFIX}${checklistId}:${fieldId}`;

const buildRecoveredRoutePath = (checklistId: string, fieldId: string): string =>
  `/checklist/${checklistId}?recoveredPhotoFieldId=${encodeURIComponent(fieldId)}`;

const normalizeError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

const isNullPendingResultError = (error: Error): boolean =>
  /Cannot read (property|properties) ['"]?length['"]? (of|from) null|Cannot read properties of null \(reading ['"]length['"]\)/i.test(
    error.message
  );

const sanitizeUri = (uri?: string | null): string | null => {
  if (!uri) {
    return null;
  }

  const withoutQuery = uri.split('?')[0]?.replace(/\\/g, '/');
  const schemeMatch = withoutQuery.match(/^([a-zA-Z0-9+.-]+):\/\//);
  const scheme = schemeMatch?.[1] ?? 'unknown';
  const fileName = withoutQuery.split('/').filter(Boolean).pop() ?? null;

  return fileName ? `${scheme}://.../${fileName}` : `${scheme}://...`;
};

const addRecoveryBreadcrumb = (
  message: string,
  level: 'error' | 'warning' | 'info',
  data: Record<string, unknown> = {}
): void => {
  addSentryBreadcrumb({
    category: 'image_picker.pending_recovery',
    message,
    level,
    data,
  });
};

const summarizeAsset = (asset?: ImagePicker.ImagePickerAsset | null) => ({
  uri: sanitizeUri(asset?.uri),
  width: asset?.width ?? null,
  height: asset?.height ?? null,
  type: asset?.type ?? null,
});

const summarizePendingResult = (
  result: ImagePicker.ImagePickerResult | ImagePicker.ImagePickerErrorResult
) => {
  if ('code' in result) {
    return {
      type: 'error',
      code: result.code,
      message: result.message ?? null,
    };
  }

  if (result.canceled) {
    return {
      type: 'cancelled',
      assetsCount: 0,
    };
  }

  return {
    type: 'success',
    assetsCount: result.assets?.length ?? 0,
    firstAsset: summarizeAsset(result.assets?.[0]),
  };
};

const getPendingCameraResults = async (): Promise<
  Array<ImagePicker.ImagePickerResult | ImagePicker.ImagePickerErrorResult>
> => {
  const pendingResults = (await ImagePicker.getPendingResultAsync()) as unknown;

  if (Array.isArray(pendingResults)) {
    return pendingResults as Array<ImagePicker.ImagePickerResult | ImagePicker.ImagePickerErrorResult>;
  }

  PendingImagePickerRecoveryLogger.Main.info(
    `[pendingCamera] getPendingResultAsync retornou ${pendingResults === null ? 'null' : typeof pendingResults}; tratando como vazio`
  );
  addRecoveryBreadcrumb('Resultado pendente de camera nulo tratado como vazio.', 'info', {
    returnedType: pendingResults === null ? 'null' : typeof pendingResults,
  });

  return [];
};

const normalizeRecoveredAsset = (
  asset: ImagePicker.ImagePickerAsset
): ImagePicker.ImagePickerAsset => {
  const assetWithExtras = asset as ImagePicker.ImagePickerAsset & {
    assetId?: string | null;
    fileName?: string | null;
    fileSize?: number | null;
    mimeType?: string | null;
  };

  return {
    uri: asset.uri,
    width: asset.width,
    height: asset.height,
    type: asset.type,
    assetId: assetWithExtras.assetId ?? null,
    fileName: assetWithExtras.fileName ?? null,
    fileSize: assetWithExtras.fileSize ?? undefined,
    mimeType: assetWithExtras.mimeType ?? undefined,
  } as ImagePicker.ImagePickerAsset;
};

const readPendingCameraContext = async (): Promise<PendingCameraContext | null> => {
  const raw = await AsyncStorage.getItem(PENDING_CAMERA_CONTEXT_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as PendingCameraContext;
  } catch (error) {
    PendingImagePickerRecoveryLogger.Main.warn(
      `[pendingCamera] contexto salvo invalido: ${normalizeError(error).message}`
    );
    addRecoveryBreadcrumb('Contexto pendente invalido removido.', 'warning', {
      error: normalizeError(error).message,
    });
    await AsyncStorage.removeItem(PENDING_CAMERA_CONTEXT_KEY);
    return null;
  }
};

export const persistPendingCameraContext = async (
  telemetry: Record<string, unknown>
): Promise<void> => {
  if (Platform.OS !== 'android') {
    return;
  }

  const checklistId = asString(telemetry.checklistId);
  const entityId = asString(telemetry.entityId);
  const context: PendingCameraContext = {
    flowId: asString(telemetry.flowId) ?? `${Date.now()}`,
    source: asString(telemetry.source),
    screen: asString(telemetry.screen),
    action: asString(telemetry.action),
    entityType: asString(telemetry.entityType),
    entityId,
    checklistId,
    empresaId: asString(telemetry.empresaId),
    routePath: checklistId ? `/checklist/${checklistId}` : null,
    createdAt: Date.now(),
  };

  try {
    await AsyncStorage.setItem(PENDING_CAMERA_CONTEXT_KEY, JSON.stringify(context));
    PendingImagePickerRecoveryLogger.Main.info(
      `[pendingCamera] contexto salvo para checklist=${context.checklistId || 'n/a'} campo=${context.entityId || 'n/a'}`
    );
    addRecoveryBreadcrumb('Contexto de camera pendente salvo.', 'info', {
      flowId: context.flowId,
      checklistId: context.checklistId,
      fieldId: context.entityId,
      empresaId: context.empresaId,
    });
  } catch (error) {
    const normalizedError = normalizeError(error);
    PendingImagePickerRecoveryLogger.Main.warn(
      `[pendingCamera] falha ao salvar contexto pendente: ${normalizedError.message}`
    );
    captureSentryExceptionWithScope(normalizedError, {
      level: 'warning',
      context: {
        flow: 'pending_image_picker_recovery',
        stage: 'persist_context',
        checklistId: context.checklistId,
        fieldId: context.entityId,
      },
      tags: {
        flow: 'pending_image_picker_recovery',
      },
      dedupeKey: `pending-camera-context-persist:${context.checklistId || 'none'}:${context.entityId || 'none'}`,
    });
  }
};

export const clearPendingCameraContext = async (
  reason: string,
  telemetry: Record<string, unknown> = {}
): Promise<void> => {
  if (Platform.OS !== 'android') {
    return;
  }

  try {
    const context = await readPendingCameraContext();
    await AsyncStorage.removeItem(PENDING_CAMERA_CONTEXT_KEY);
    PendingImagePickerRecoveryLogger.Main.info(
      `[pendingCamera] contexto removido (${reason}); tinhaContexto=${Boolean(context)}`
    );
    addRecoveryBreadcrumb('Contexto de camera pendente removido.', 'info', {
      reason,
      hadContext: Boolean(context),
      checklistId: context?.checklistId ?? asString(telemetry.checklistId),
      fieldId: context?.entityId ?? asString(telemetry.entityId),
    });
  } catch (error) {
    const normalizedError = normalizeError(error);
    PendingImagePickerRecoveryLogger.Main.warn(
      `[pendingCamera] falha ao limpar contexto pendente: ${normalizedError.message}`
    );
    captureSentryExceptionWithScope(normalizedError, {
      level: 'warning',
      context: {
        flow: 'pending_image_picker_recovery',
        stage: 'clear_context',
        reason,
      },
      tags: {
        flow: 'pending_image_picker_recovery',
      },
      dedupeKey: `pending-camera-context-clear:${reason}`,
    });
  }
};

export const recoverPendingCameraResult = async (
  trigger: string,
  metadata: Record<string, unknown> = {}
): Promise<PendingRecoveryOutcome> => {
  if (Platform.OS !== 'android') {
    return { status: 'unsupported' };
  }

  let context: PendingCameraContext | null = null;

  try {
    context = await readPendingCameraContext();
    if (!context) {
      return { status: 'no_context' };
    }

    const pendingResults = await getPendingCameraResults();
    const ageMs = Date.now() - context.createdAt;

    PendingImagePickerRecoveryLogger.Main.info(
      `[pendingCamera] trigger=${trigger} resultados=${pendingResults.length} idadeMs=${ageMs}`
    );
    addRecoveryBreadcrumb('Resultado pendente de camera verificado.', 'info', {
      trigger,
      ageMs,
      resultsCount: pendingResults.length,
      results: pendingResults.map(summarizePendingResult),
      metadata,
    });

    if (pendingResults.length === 0) {
      if (ageMs > PENDING_CONTEXT_TTL_MS) {
        await clearPendingCameraContext('pending_context_stale', metadata);
      }
      return { status: 'empty', context };
    }

    const successResult = pendingResults.find(
      (result): result is ImagePicker.ImagePickerSuccessResult =>
        !('code' in result) && !result.canceled && Boolean(result.assets?.[0])
    );

    if (!successResult || !context.checklistId || !context.entityId) {
      PendingImagePickerRecoveryLogger.Main.warn(
        `[pendingCamera] resultado ignorado; sucesso=${Boolean(successResult)} checklist=${Boolean(context.checklistId)} campo=${Boolean(context.entityId)}`
      );
      addRecoveryBreadcrumb('Resultado pendente de camera ignorado.', 'warning', {
        trigger,
        hasSuccessResult: Boolean(successResult),
        hasChecklistId: Boolean(context.checklistId),
        hasFieldId: Boolean(context.entityId),
        results: pendingResults.map(summarizePendingResult),
      });
      await clearPendingCameraContext('pending_result_ignored', metadata);
      return { status: 'ignored', context };
    }

    const routePath = buildRecoveredRoutePath(context.checklistId, context.entityId);
    const record: RecoveredCameraAssetRecord = {
      context,
      asset: normalizeRecoveredAsset(successResult.assets[0]),
      recoveredAt: Date.now(),
    };

    await AsyncStorage.setItem(
      buildRecoveredCameraAssetKey(context.checklistId, context.entityId),
      JSON.stringify(record)
    );
    await clearPendingCameraContext('pending_result_stored', metadata);

    PendingImagePickerRecoveryLogger.Main.info(
      `[pendingCamera] asset recuperado para checklist=${context.checklistId} campo=${context.entityId}`
    );
    addRecoveryBreadcrumb('Asset de camera pendente armazenado para consumo.', 'info', {
      trigger,
      routePath,
      checklistId: context.checklistId,
      fieldId: context.entityId,
      asset: summarizeAsset(record.asset),
    });

    return { status: 'stored', context, routePath };
  } catch (error) {
    const normalizedError = normalizeError(error);
    if (context && isNullPendingResultError(normalizedError)) {
      PendingImagePickerRecoveryLogger.Main.info(
        `[pendingCamera] getPendingResultAsync falhou sem resultado pendente; trigger=${trigger}`
      );
      addRecoveryBreadcrumb('Resultado pendente de camera nulo tratado como vazio.', 'info', {
        trigger,
        metadata,
        error: normalizedError.message,
      });
      return { status: 'empty', context };
    }

    PendingImagePickerRecoveryLogger.Main.warn(
      `[pendingCamera] falha ao recuperar resultado pendente: ${normalizedError.message}`
    );
    captureSentryExceptionWithScope(normalizedError, {
      level: 'error',
      context: {
        flow: 'pending_image_picker_recovery',
        stage: 'recover_result',
        trigger,
        metadata,
      },
      tags: {
        flow: 'pending_image_picker_recovery',
      },
      dedupeKey: `pending-camera-recovery:${trigger}`,
    });
    return { status: 'failed', error: normalizedError };
  }
};

export const getRecoveredCameraAsset = async (
  checklistId: string,
  fieldId: string,
  empresaId?: string
): Promise<RecoveredCameraAssetRecord | null> => {
  if (Platform.OS !== 'android') {
    return null;
  }

  const key = buildRecoveredCameraAssetKey(checklistId, fieldId);
  const raw = await AsyncStorage.getItem(key);
  if (!raw) {
    return null;
  }

  try {
    const record = JSON.parse(raw) as RecoveredCameraAssetRecord;
    const recoveredAgeMs = Date.now() - record.recoveredAt;

    if (recoveredAgeMs > RECOVERED_ASSET_TTL_MS) {
      await AsyncStorage.removeItem(key);
      PendingImagePickerRecoveryLogger.Main.warn(
        `[pendingCamera] asset recuperado expirado para checklist=${checklistId} campo=${fieldId}`
      );
      addRecoveryBreadcrumb('Asset de camera pendente expirado.', 'warning', {
        checklistId,
        fieldId,
        empresaId: empresaId ?? record.context.empresaId,
        recoveredAgeMs,
      });
      return null;
    }

    PendingImagePickerRecoveryLogger.Main.info(
      `[pendingCamera] asset pronto para processamento checklist=${checklistId} campo=${fieldId}`
    );
    addRecoveryBreadcrumb('Asset de camera pendente consumido.', 'info', {
      checklistId,
      fieldId,
      empresaId: empresaId ?? record.context.empresaId,
      recoveredAgeMs,
      asset: summarizeAsset(record.asset),
    });
    return record;
  } catch (error) {
    await AsyncStorage.removeItem(key);
    const normalizedError = normalizeError(error);
    PendingImagePickerRecoveryLogger.Main.warn(
      `[pendingCamera] asset recuperado invalido: ${normalizedError.message}`
    );
    captureSentryExceptionWithScope(normalizedError, {
      level: 'warning',
      context: {
        flow: 'pending_image_picker_recovery',
        stage: 'consume_asset',
        checklistId,
        fieldId,
        empresaId,
      },
      tags: {
        flow: 'pending_image_picker_recovery',
      },
      dedupeKey: `pending-camera-consume:${checklistId}:${fieldId}`,
    });
    return null;
  }
};

export const clearRecoveredCameraAsset = async (
  checklistId: string,
  fieldId: string
): Promise<void> => {
  if (Platform.OS !== 'android') {
    return;
  }

  await AsyncStorage.removeItem(buildRecoveredCameraAssetKey(checklistId, fieldId));
  PendingImagePickerRecoveryLogger.Main.info(
    `[pendingCamera] asset recuperado removido checklist=${checklistId} campo=${fieldId}`
  );
  addRecoveryBreadcrumb('Asset de camera pendente removido apos processamento.', 'info', {
    checklistId,
    fieldId,
  });
};
